<script setup>
import { ref, watch, computed } from "vue";
import { useItemListStore } from "../plugins/store/itemsList.store";
import { useHistoricStore } from "../plugins/store/historic.store";
import { useSelectedNodeStore } from "@templates/components/treeView-vue3/store/selected-node.store";
import { fetchAndSelectItem } from "@templates/components/treeView-vue3/helpers";
import { pubsub } from "@assets/js/imaios-global/imaios-global";
import { TREE_VIEW_NOTIFICATIONS } from "@templates/components/structures/treeView/notifications";
import TagNomenclature from "@templates/components/tag/TagNomenclature.vue";

const { item, parentList, displayTag } = defineProps({
  item: Object,
  parentList: Array | [],
  displayTag: Boolean | false,
});
const itemListStore = useItemListStore();
const historicStore = useHistoricStore();
const selectedNodeStore = useSelectedNodeStore();
const getCurrentTaId = computed(() => Number(document.getElementById('tree-view-anatomical').dataset.treeTaid));
const getCurrentApplication = computed(() => document.getElementById('anatomical-frontpage-content').dataset.applicationUrl.replace("-", ""));

async function visitItem(item, visitParent = false) {
  pubsub.publish("closeSearchDropdown");

  const urlToVisit = visitParent ? item.parentPath : item.url;
  const nodePathToCheck = visitParent ? getParentNodePath(item.nodePath) : item.nodePath;

  if (selectedNodeStore.isSelectedNode(nodePathToCheck)) return;

  if (!visitParent) historicStore.visitItem(item); // Not add to historic if visitParent

  // Force redirect location if suggest item is in different application (ex: vetanatomy -> eanatomy)
  const pathnameParts = urlToVisit.split("/");
  const incomingApplication = pathnameParts[2].replace("-", "");

  if (getCurrentApplication.value !== incomingApplication)
    return (window.location = urlToVisit);

  //visit item in another Tree
  if (getCurrentTaId.value !== item.taId) {
    pubsub.publish(TREE_VIEW_NOTIFICATIONS.CURRENT_TA_CHANGE, item.taId);
  }

  await fetchAndSelectItem('anatomical', urlToVisit, true, false);
}

//region RENDERING UTILS
const highlightTerms = (input) =>
  input.replace(
    new RegExp("(^|\\s)(" + item.highlightTerms.join("|") + ")($|\\s)", "ig"),
    "$1<strong>$2</strong>$3"
  );

const showLatinTitle = item.titleLatin !== item.title;
const showParent = item.parentTitle && item.parentPath && !parentList;
//endregion

//region ACTIVE
const isActive = () => item.hash === itemListStore.getActiveItem?.hash;

const onMouseEnter = () => {
  itemListStore.setActiveItem(item);
};
const rootEl = ref(null);
watch(
  () => isActive(),
  (isActive) => {
    if (isActive) {
      rootEl.value.scrollIntoView({ behavior: "instant", block: "nearest" });
    }
  }
);
//endregion

function getParentNodePath(nodePath) {
  return nodePath.replace(/\/[^/]+\/?$/, "/");
}
</script>

<template>
  <div
    :class="[{ active: isActive() }, 'item-search', 'position-relative']"
    ref="rootEl"
    @mouseenter="onMouseEnter"
  >
    <div class="icon-container">
      <div
        class="icon-search"
        :style="`background-image: url('${item.iconPath}')`"
      />
    </div>
    <div class="content-search">
      <p class="search-title">
      <a
        class="search-title-link stretched-link"
        :href="item.url"
          @click.prevent="visitItem(item)"
      >
          <span class="language-one" v-html="highlightTerms(item.title)" />
          <template v-if="showLatinTitle">
            -
            <span
              class="language-two"
              v-html="highlightTerms(item.titleLatin)"
            />
          </template>
        </a>
        </p>
      <div v-if="parentList" class="search-hierarchy">
        <p class="parent-tag-title" v-for="parent in parentList">
          <TagNomenclature v-for="taId in parent.tag" :id="taId" />
          <a
            :href="parent.item.parentPath"
            @click.prevent="visitItem(parent.item, true)"
          >
            <span>{{ parent.title }}</span>
      </a>
        </p>
      </div>
      <div v-else-if="showParent" class="search-hierarchy">
        <p class="parent-tag-title">
          <TagNomenclature v-if="displayTag" :id="item.taId" />
          <a :href="item.parentPath" @click.prevent="visitItem(item, true)">
          <span>{{ item.parentTitle }}</span>
        </a>
      </p>
    </div>
  </div>
  </div>
</template>

<style>
.search-title,
.search-hierarchy {
  margin: 0;
  a {
    text-decoration: underline;
  }
}

.search-hierarchy {
  display: flex;
  gap: 4px;
  flex-flow: wrap;
}

.parent-tag-title {
  display: flex;
  gap: 4px;
  align-content: center;
  padding-right: 0.5rem;
  border-right: 1px solid var(--com-divider-border-secondary);
  margin: 0 0.5rem 0 0;

  a {
    margin-left: 4px;
  }

  &:last-child {
    border-right: none;
    margin: 0;
  }
}
</style>
